import {
  ChangeEvent,
  MutableRefObject,
  startTransition,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import axios from "axios";
import { format } from "date-fns";
import styled from "@emotion/styled";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import Iconify from "../../minimals/components/iconify";
import { HEADER } from "../../minimals/config-global";
import useOffSetTop from "../../minimals/hooks/useOffSetTop";
import { ActionButtonGroup, SegmentedButtons } from "./components/DocumentActionButtons";
import { DocumentAiReview } from "./components/DocumentAiReview";
import {
  CategoryNavigator,
  CategorySection,
  DateDocumentsSection,
  MobileDocumentsSection,
  getMobileCategoryDomId,
  getMobileSubcategoryDomId
} from "./components/DocumentSections";
import { OrderSummaryCard, statusColorMap } from "./components/OrderSummaryCard";
import { NAV_RAIL_WIDTH, OrderDocumentsNavRail } from "./components/OrderDocumentsNavRail";
import { OrderSearchHome } from "./components/OrderSearchHome";
import { HeaderSlotContext } from "../components/layouts/dashboard/HeaderSlotContext";
import { ContactsDrawer } from "./drawers/ContactsDrawer";
import { EmailDrawer } from "./drawers/EmailDrawer";
import { NotesDrawer } from "./drawers/NotesDrawer";
import { OrderHistoryDrawer } from "./drawers/OrderHistoryDrawer";
import { UploadDrawer } from "./drawers/UploadDrawer";
import {
  DocumentsOrderContact,
  DocumentsOrderInfo,
  DocumentsOrderNote,
  OrderDocumentCategory,
  OrderDocumentDetail
} from "./types";
import useDebounce from "../../hooks/useDebounce";
import { logEvent } from "../utils/analyticsLogger";
import { getAxiosErrorMessage, isResolvableOrderAlertNote } from "./utils/orderDocumentAlerts";

const getSubcategoryKey = (categoryName: string, subcategoryId: string) => `${categoryName}:${subcategoryId}`;
// Must match the key CategorySection registers itself under in DocumentSections.
const getCategoryKey = (categoryName: string) => `cat::${categoryName}`;

// The dashboard header shrinks from H_DASHBOARD_DESKTOP to H_DASHBOARD_DESKTOP_OFFSET once the
// page is scrolled past the header height. Sticky columns/toolbars must track that height so they
// stay flush under the header instead of leaving a gap that scrolling content shows through.
const useStickyHeaderTop = () => {
  const isHeaderOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP);
  return isHeaderOffset ? HEADER.H_DASHBOARD_DESKTOP_OFFSET : HEADER.H_DASHBOARD_DESKTOP;
};

const DocumentsNotFound = styled.h1`
  color: #1f2b5e;
  font-size: 40px;
  left: 0;
  line-height: 1.3;
  margin: 0;
  position: absolute;
  text-align: center;
  top: 40%;
  transform: translateY(-50%);
  width: 100%;

  @media (min-width: 900px) {
    font-size: 50px;
  }
`;

type DocumentShippingStatus = {
  documentId: number;
  shippingStatus: string;
};

type DocumentFileExistsStatus = {
  documentId: number;
  fileExists: boolean;
};

type DocumentTrackingNumberStatus = {
  documentId: number;
  trackingNumber: string;
};

type BarcodeOption = {
  description: string;
  subCatId: string;
  nonPublic: boolean;
};

type TeamsChannelResult = {
  id: string;
  displayName: string;
  webUrl: string;
};

const formatDate = (value: string) => {
  if (!value) {
    return "";
  }

  return format(new Date(value), "MM/dd/yyyy h:mm a");
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const writeTeamsLoadingPage = (teamsWindow: Window, channelName: string) => {
  teamsWindow.document.open();
  teamsWindow.document.write(`<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Opening Teams</title>
  <style>
    :root {
      color-scheme: light;
      --titleq-navy: #091a7a;
      --titleq-muted: #627684;
      --titleq-border: #d9dfe6;
      --titleq-bg: #f5f7fa;
    }
    * {
      box-sizing: border-box;
    }
    body {
      margin: 0;
      min-height: 100vh;
      display: grid;
      place-items: center;
      background: var(--titleq-bg);
      color: #1f2933;
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    main {
      width: min(440px, calc(100vw - 32px));
      padding: 32px;
      border: 1px solid var(--titleq-border);
      border-radius: 12px;
      background: #fff;
      box-shadow: 0 4px 14px rgba(22, 28, 36, 0.08);
      text-align: center;
    }
    .spinner {
      width: 34px;
      height: 34px;
      margin: 0 auto 20px;
      border: 3px solid #dfe3ff;
      border-top-color: var(--titleq-navy);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    h1 {
      margin: 0 0 10px;
      color: var(--titleq-navy);
      font-size: 22px;
      line-height: 1.25;
    }
    p {
      margin: 0;
      color: var(--titleq-muted);
      font-size: 14px;
      line-height: 1.5;
    }
    .channel {
      margin-top: 16px;
      padding: 10px 12px;
      border: 1px solid var(--titleq-border);
      border-radius: 8px;
      color: var(--titleq-navy);
      font-size: 15px;
      font-weight: 700;
      overflow-wrap: anywhere;
      background: #fafbff;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  </style>
</head>
<body>
  <main>
    <div class="spinner" aria-hidden="true"></div>
    <h1>Opening Teams</h1>
    <p>One moment...</p>
    <div class="channel">${escapeHtml(channelName)}</div>
  </main>
</body>
</html>`);
  teamsWindow.document.close();
};

const getDocumentSearchText = (document: OrderDocumentDetail, categoryName: string, subcategoryName: string) =>
  [
    document.documentDescription,
    document.descriptionOnly,
    document.additionalDescription,
    document.userName,
    categoryName,
    subcategoryName
  ]
    .filter(Boolean)
    .join("\n")
    .toLowerCase();

const buildDocumentSearchIndex = (categories: OrderDocumentCategory[]) => {
  const searchTextByDocumentId = new Map<number, string>();

  categories.forEach((category) => {
    category.subcategories.forEach((subcategory) => {
      subcategory.documents.forEach((document) => {
        searchTextByDocumentId.set(document.id, getDocumentSearchText(document, category.name, subcategory.name));
      });
    });
  });

  return searchTextByDocumentId;
};

const filterCategories = (
  categories: OrderDocumentCategory[],
  searchText: string,
  searchTextByDocumentId: Map<number, string>
) => {
  const normalized = searchText.trim().toLowerCase();
  if (!normalized) {
    return categories;
  }

  return categories
    .map((category) => ({
      ...category,
      subcategories: category.subcategories
        .map((subcategory) => ({
          ...subcategory,
          documents: subcategory.documents.filter((document) =>
            searchTextByDocumentId.get(document.id)?.includes(normalized)
          )
        }))
        .filter((subcategory) => subcategory.documents.length > 0)
    }))
    .filter((category) => category.subcategories.length > 0);
};

const getAllDocuments = (categories: OrderDocumentCategory[]) =>
  categories.flatMap((category) => category.subcategories).flatMap((subcategory) => subcategory.documents);

const getDocumentDateTime = (document: OrderDocumentDetail) => {
  const time = new Date(document.date1).getTime();
  return Number.isNaN(time) ? 0 : time;
};

// Select stores related orders as a comma-separated display string. Commas inside a parenthesized
// description are part of that description, not separators between related orders.
const getRelatedOrders = (relatedOrders?: string | null) =>
  relatedOrders
    ?.split(/,(?![^()]*\))/)
    .map((relatedOrder) => relatedOrder.trim())
    .filter(Boolean) ?? [];

const applyShippingStatuses = (categories: OrderDocumentCategory[], shippingStatuses: DocumentShippingStatus[]) => {
  const shippingStatusById = new Map(shippingStatuses.map((status) => [status.documentId, status.shippingStatus]));

  return categories.map((category) => ({
    ...category,
    subcategories: category.subcategories.map((subcategory) => ({
      ...subcategory,
      documents: subcategory.documents.map((document) =>
        shippingStatusById.has(document.id)
          ? { ...document, shippingStatus: shippingStatusById.get(document.id) }
          : document
      )
    }))
  }));
};

const applyFileExistsStatuses = (
  categories: OrderDocumentCategory[],
  fileExistsStatuses: DocumentFileExistsStatus[]
) => {
  const fileExistsById = new Map(fileExistsStatuses.map((status) => [status.documentId, status.fileExists]));

  return categories.map((category) => ({
    ...category,
    subcategories: category.subcategories.map((subcategory) => ({
      ...subcategory,
      documents: subcategory.documents.map((document) =>
        fileExistsById.has(document.id) ? { ...document, fileExists: fileExistsById.get(document.id) } : document
      )
    }))
  }));
};

const applyTrackingNumbers = (
  categories: OrderDocumentCategory[],
  trackingNumberStatuses: DocumentTrackingNumberStatus[]
) => {
  const trackingNumberById = new Map(
    trackingNumberStatuses.map((status) => [status.documentId, status.trackingNumber])
  );

  return categories.map((category) => ({
    ...category,
    subcategories: category.subcategories.map((subcategory) => ({
      ...subcategory,
      documents: subcategory.documents.map((document) =>
        trackingNumberById.has(document.id)
          ? { ...document, trackingNumber: trackingNumberById.get(document.id) }
          : document
      )
    }))
  }));
};

export default function OrderDocumentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const stickyTop = useStickyHeaderTop();
  const navigate = useNavigate();
  const { orderNumber: orderNumberParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderNumber = orderNumberParam?.trim() ?? "";
  const searchQuery = searchParams.get("query") ?? "";
  const [orderNumberInput, setOrderNumberInput] = useState("");
  const [categories, setCategories] = useState<OrderDocumentCategory[]>([]);
  const [orderInfo, setOrderInfo] = useState<DocumentsOrderInfo | null>(null);
  const [relatedOrders, setRelatedOrders] = useState<string[]>([]);
  const [orderInfoLoading, setOrderInfoLoading] = useState(false);
  const [loadedOrderInfoNumber, setLoadedOrderInfoNumber] = useState("");
  const [orderNotFound, setOrderNotFound] = useState(false);
  const [contacts, setContacts] = useState<DocumentsOrderContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadedDocumentsOrderNumber, setLoadedDocumentsOrderNumber] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [documentViewMode, setDocumentViewMode] = useState<"category" | "date">("category");
  const [collapsedCategoryNames, setCollapsedCategoryNames] = useState<Set<string>>(new Set());
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<number>>(new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isPageFileDragging, setIsPageFileDragging] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => window.localStorage.getItem("documents-sidebar-collapsed") === "true"
  );

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((current) => {
      window.localStorage.setItem("documents-sidebar-collapsed", String(!current));
      return !current;
    });
  }, []);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // The sticky action bar (warning banners + toolbar) grows with each banner, so its
  // height is measured at scroll time to offset category jumps beneath it.
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const pageFileDragDepth = useRef(0);

  useEffect(() => {
    setOrderNumberInput("");
    setSearchText("");
  }, [orderNumber]);

  useEffect(() => {
    if (orderNumber) {
      logEvent("Documents Page", "Order Viewed", orderNumber);
    }
  }, [orderNumber]);

  const loadDocuments = useCallback(async () => {
    if (!orderNumber) {
      setCategories([]);
      setLoadedDocumentsOrderNumber("");
      setOrderInfo(null);
      setLoadedOrderInfoNumber("");
      setOrderNotFound(false);
      setRelatedOrders([]);
      setContacts([]);
      setContactsError(null);
      setContactsLoading(false);
      setOrderInfoLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setCategories([]);
    setLoadedDocumentsOrderNumber("");

    try {
      const { data } = await axios.get<OrderDocumentCategory[]>(
        `/api/orderdocuments/getall/${encodeURIComponent(orderNumber)}`
      );
      setCategories(data);
      setLoadedDocumentsOrderNumber(orderNumber);
      setSelectedDocumentIds(new Set());

      const allDocuments = getAllDocuments(data);
      if (allDocuments.length) {
        axios
          .post<DocumentFileExistsStatus[]>(
            "/api/orderdocuments/fileexists",
            { documentIds: allDocuments.map((document) => document.id) },
            { headers: { "x-suppress-global-error": "true" } }
          )
          .then(({ data: fileExistsStatuses }) => {
            setCategories((current) => applyFileExistsStatuses(current, fileExistsStatuses || []));
          })
          .catch(() => {});
      }

      if (allDocuments.length) {
        axios
          .post<DocumentTrackingNumberStatus[]>(
            "/api/orderdocuments/trackingnumbers",
            { documentIds: allDocuments.map((document) => document.id) },
            { headers: { "x-suppress-global-error": "true" } }
          )
          .then(({ data: trackingNumberStatuses }) => {
            const nextTrackingNumberStatuses = trackingNumberStatuses || [];
            const trackingNumberById = new Map(
              nextTrackingNumberStatuses.map((status) => [status.documentId, status.trackingNumber])
            );
            setCategories((current) => applyTrackingNumbers(current, nextTrackingNumberStatuses));

            const documentsWithTracking = allDocuments
              .map((document) => ({
                ...document,
                trackingNumber: trackingNumberById.get(document.id)
              }))
              .filter((document) => document.trackingNumber);

            if (!documentsWithTracking.length) {
              return;
            }

            axios
              .post<DocumentShippingStatus[]>(
                "/api/orderdocuments/getShippingStatus",
                documentsWithTracking.map((document) => ({
                  documentId: document.id,
                  trackingNumber: document.trackingNumber,
                  shippingStatus: ""
                })),
                { headers: { "x-suppress-global-error": "true" } }
              )
              .then(({ data: shippingStatuses }) => {
                setCategories((current) => applyShippingStatuses(current, shippingStatuses || []));
              })
              .catch(() => {});
          })
          .catch(() => {});
      }
    } catch {
      setError("Documents could not be loaded for this order.");
      setCategories([]);
      setLoadedDocumentsOrderNumber(orderNumber);
    } finally {
      setLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const loadOrderInfo = useCallback(async () => {
    if (!orderNumber) {
      return;
    }

    setOrderInfoLoading(true);
    setOrderInfo(null);
    setLoadedOrderInfoNumber("");
    setOrderNotFound(false);
    setRelatedOrders([]);

    try {
      const { data: nextOrderInfo } = await axios.get<DocumentsOrderInfo>(
        `/api/orderdocuments/orderinfo?orderNumber=${encodeURIComponent(orderNumber)}`
      );

      if (!nextOrderInfo?.number) {
        setOrderNotFound(true);
        setLoadedOrderInfoNumber(orderNumber);
        setRelatedOrders([]);
        return;
      }

      setOrderInfo(nextOrderInfo);
      setLoadedOrderInfoNumber(orderNumber);
      setRelatedOrders(getRelatedOrders(nextOrderInfo?.relatedOrders));
    } catch {
      setOrderInfo(null);
      setLoadedOrderInfoNumber(orderNumber);
      setOrderNotFound(true);
      setRelatedOrders([]);
    } finally {
      setOrderInfoLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    loadOrderInfo();
  }, [loadOrderInfo]);

  const loadContacts = useCallback(async () => {
    if (!orderNumber) {
      setContacts([]);
      setContactsError(null);
      setContactsLoading(false);
      return;
    }

    setContactsLoading(true);
    setContactsError(null);
    setContacts([]);

    try {
      const { data } = await axios.get<DocumentsOrderContact[]>(
        `/api/orderdocuments/contacts?orderNumber=${encodeURIComponent(orderNumber)}`
      );
      setContacts(data ?? []);
    } catch {
      setContacts([]);
      setContactsError("Contacts could not be loaded for this order.");
    } finally {
      setContactsLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Keep the search input responsive while a large document set is being filtered and rendered.
  const deferredSearchText = useDeferredValue(searchText);
  const documentSearchIndex = useMemo(() => buildDocumentSearchIndex(categories), [categories]);
  const filteredCategories = useMemo(
    () => filterCategories(categories, deferredSearchText, documentSearchIndex),
    [categories, deferredSearchText, documentSearchIndex]
  );
  const allDocuments = useMemo(() => getAllDocuments(categories), [categories]);
  const documentById = useMemo(() => new Map(allDocuments.map((document) => [document.id, document])), [allDocuments]);
  const visibleDocuments = useMemo(() => getAllDocuments(filteredCategories), [filteredCategories]);
  const orderInfoPending = Boolean(orderNumber) && loadedOrderInfoNumber !== orderNumber;
  const selectableVisibleDocuments = useMemo(
    () => visibleDocuments.filter((document) => document.fileExists !== false),
    [visibleDocuments]
  );
  const selectedDocuments = useMemo(
    () => allDocuments.filter((document) => selectedDocumentIds.has(document.id) && document.fileExists !== false),
    [allDocuments, selectedDocumentIds]
  );

  const dateSortedDocuments = useMemo(
    () => [...visibleDocuments].sort((first, second) => getDocumentDateTime(second) - getDocumentDateTime(first)),
    [visibleDocuments]
  );

  const allVisibleSelected =
    selectableVisibleDocuments.length > 0 &&
    selectableVisibleDocuments.every((document) => selectedDocumentIds.has(document.id));

  const toggleAllVisibleDocuments = useCallback(() => {
    setSelectedDocumentIds((current) => {
      const next = new Set(current);
      if (selectableVisibleDocuments.every((document) => next.has(document.id))) {
        selectableVisibleDocuments.forEach((document) => next.delete(document.id));
      } else {
        selectableVisibleDocuments.forEach((document) => next.add(document.id));
      }
      return next;
    });
  }, [selectableVisibleDocuments]);

  const toggleDocumentSelection = useCallback(
    (documentId: number, selected: boolean) => {
      const document = documentById.get(documentId);
      if (document?.fileExists === false) {
        return;
      }

      setSelectedDocumentIds((current) => {
        const next = new Set(current);
        if (selected) {
          next.add(documentId);
        } else {
          next.delete(documentId);
        }
        return next;
      });
    },
    [documentById]
  );

  const toggleDocumentSelections = useCallback(
    (documentIds: number[], selected: boolean) => {
      setSelectedDocumentIds((current) => {
        const next = new Set(current);
        documentIds.forEach((documentId) => {
          const document = documentById.get(documentId);
          if (document?.fileExists === false) {
            return;
          }

          if (selected) {
            next.add(documentId);
          } else {
            next.delete(documentId);
          }
        });
        return next;
      });
    },
    [documentById]
  );

  const removeSelectedDocument = useCallback((documentId: number) => {
    setSelectedDocumentIds((current) => {
      const next = new Set(current);
      next.delete(documentId);
      return next;
    });
  }, []);

  const updateDocumentViewMode = useCallback((value: "category" | "date") => {
    startTransition(() => {
      setDocumentViewMode(value);
    });
  }, []);

  const navigateToSearchOrder = useCallback(
    (nextOrderNumber: string) => {
      navigate(`/documents/${encodeURIComponent(nextOrderNumber)}`);
    },
    [navigate]
  );

  // From an order page, opening another order goes to a new tab so the current
  // order stays put (same as the Related Orders links).
  const openOrderInNewTab = useCallback((nextOrderNumber: string) => {
    window.open(`/documents/${encodeURIComponent(nextOrderNumber)}`, "_blank", "noopener,noreferrer");
  }, []);

  const updateSearchQuery = useCallback(
    (query: string) => {
      if (query) {
        setSearchParams({ query }, { replace: true });
      } else if (!orderNumber) {
        setSearchParams({}, { replace: true });
      }
    },
    [orderNumber, setSearchParams]
  );

  const toggleCategory = useCallback((categoryName: string) => {
    setCollapsedCategoryNames((current) => {
      const next = new Set(current);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  }, []);

  // Scroll the section into view, offset below the app header (stickyTop) plus the
  // sticky action bar's *measured* height, so extra warning banners don't leave the
  // target hidden underneath it.
  const scrollToSectionKey = useCallback(
    (key: string) => {
      if (documentViewMode !== "category") {
        updateDocumentViewMode("category");
      }

      let attempts = 0;
      const tryScroll = () => {
        const element = sectionRefs.current[key];
        if (!element) {
          attempts += 1;
          if (attempts < 20) {
            window.setTimeout(tryScroll, 50);
          }
          return;
        }

        const stickyHeight = stickyHeaderRef.current?.getBoundingClientRect().height ?? 0;
        element.style.scrollMarginTop = `${stickyTop + stickyHeight + 12}px`;
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      };

      window.setTimeout(tryScroll, 60);
    },
    [documentViewMode, stickyTop, updateDocumentViewMode]
  );

  const scrollToCategory = useCallback(
    (categoryName: string) => {
      setCollapsedCategoryNames((current) => {
        const next = new Set(current);
        next.delete(categoryName);
        return next;
      });

      scrollToSectionKey(getCategoryKey(categoryName));
    },
    [scrollToSectionKey]
  );

  const scrollToSubcategory = useCallback(
    (categoryName: string, subcategoryId: string) => {
      setCollapsedCategoryNames((current) => {
        const next = new Set(current);
        next.delete(categoryName);
        return next;
      });

      scrollToSectionKey(getSubcategoryKey(categoryName, subcategoryId));
    },
    [scrollToSectionKey]
  );

  const openUploadDrawer = useCallback(() => {
    setUploadFile(null);
    setUploadOpen(true);
  }, []);

  const closeUploadDrawer = useCallback(() => {
    setUploadOpen(false);
    setUploadFile(null);
  }, []);

  useEffect(() => {
    if (!orderNumber) {
      return;
    }

    const hasFiles = (event: globalThis.DragEvent) => event.dataTransfer?.types.includes("Files");
    const handleDragEnter = (event: globalThis.DragEvent) => {
      if (!hasFiles(event)) {
        return;
      }

      event.preventDefault();
      pageFileDragDepth.current += 1;
      setIsPageFileDragging(true);
    };
    const handleDragOver = (event: globalThis.DragEvent) => {
      if (!hasFiles(event)) {
        return;
      }

      event.preventDefault();
      event.dataTransfer!.dropEffect = "copy";
    };
    const handleDragLeave = (event: globalThis.DragEvent) => {
      if (!hasFiles(event)) {
        return;
      }

      pageFileDragDepth.current = Math.max(0, pageFileDragDepth.current - 1);
      if (pageFileDragDepth.current === 0) {
        setIsPageFileDragging(false);
      }
    };
    const handleDrop = (event: globalThis.DragEvent) => {
      if (!hasFiles(event)) {
        return;
      }

      event.preventDefault();
      pageFileDragDepth.current = 0;
      setIsPageFileDragging(false);

      const file = event.dataTransfer?.files?.[0];
      if (file) {
        setUploadFile(file);
        setUploadOpen(true);
      }
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, [orderNumber]);

  return (
    <>
      <Helmet>
        <title>{orderNumber || "Documents | TitleQ"}</title>
      </Helmet>

      {isPageFileDragging && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal - 1,
            display: "grid",
            placeItems: "center",
            pointerEvents: "none",
            backgroundColor: (theme) => alpha(theme.palette.primary.dark, 0.14)
          }}
        >
          <Paper
            elevation={0}
            sx={{
              minWidth: 280,
              px: 4,
              py: 3,
              textAlign: "center",
              border: "2px dashed",
              borderColor: "primary.main",
              borderRadius: 1,
              backgroundColor: "background.paper"
            }}
          >
            <Iconify icon="eva:cloud-upload-fill" width={36} sx={{ color: "primary.main", mb: 1 }} />
            <Typography variant="h6" sx={{ color: "primary.dark", fontWeight: 700 }}>
              Drop file to upload
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The Upload drawer will open with your file selected.
            </Typography>
          </Paper>
        </Box>
      )}

      <OrderDocumentsNavRail orderNumber={orderNumber} orderInfo={orderInfo} />

      <Container
        maxWidth={false}
        sx={{
          // Desktop pulls content up under the taller dashboard header; mobile must not,
          // or the fixed 64px header covers the top of the first card.
          mt: { xs: 0, lg: -10 },
          mb: { xs: -7, lg: -10.5 },
          px: { xs: 1.5, md: 2.5 },
          pl: { lg: `${NAV_RAIL_WIDTH + 8}px` }
        }}
      >
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          {!orderNumber && (
            <OrderSearchHome
              initialQuery={searchQuery}
              onQueryChange={updateSearchQuery}
              onOpenOrder={navigateToSearchOrder}
              stickyTop={stickyTop}
            />
          )}

          {orderNumber &&
            (isMobile ? (
              <MobileOrderDocumentsWorkspace
                orderNumber={orderNumber}
                orderInfo={orderInfo}
                relatedOrders={relatedOrders}
                orderInfoLoading={orderInfoLoading}
                orderNumberInput={orderNumberInput}
                onOrderNumberInputChange={setOrderNumberInput}
                onOpenOrder={openOrderInNewTab}
                documentViewMode={documentViewMode}
                onDocumentViewModeChange={updateDocumentViewMode}
                searchText={searchText}
                onSearchTextChange={setSearchText}
                filteredCategories={filteredCategories}
                dateSortedDocuments={dateSortedDocuments}
                loading={loading}
                orderInfoPending={orderInfoPending}
                orderNotFound={orderNotFound}
                selectedDocumentIds={selectedDocumentIds}
                selectedDocuments={selectedDocuments}
                allVisibleDocumentsSelected={allVisibleSelected}
                onToggleAllVisibleDocuments={toggleAllVisibleDocuments}
                onToggleDocumentSelection={toggleDocumentSelection}
                onRemoveSelectedDocument={removeSelectedDocument}
                onDocumentsUploaded={loadDocuments}
                uploadOpen={uploadOpen}
                uploadFile={uploadFile}
                onOpenUpload={openUploadDrawer}
                onCloseUpload={closeUploadDrawer}
                onOrderInfoUpdated={loadOrderInfo}
                onContactsChanged={async () => {
                  await Promise.all([loadContacts(), loadOrderInfo()]);
                }}
                contacts={contacts}
                contactsLoading={contactsLoading}
                contactsError={contactsError}
              />
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    lg: sidebarCollapsed ? "0px minmax(0, 1fr)" : "340px minmax(0, 1fr)"
                  },
                  gap: { xs: 1.5, lg: sidebarCollapsed ? 0 : 3 },
                  alignItems: "start",
                  transition: (theme) =>
                    theme.transitions.create("grid-template-columns", {
                      duration: theme.transitions.duration.shorter
                    })
                }}
              >
                <Stack
                  spacing={2}
                  sx={{
                    position: { lg: "sticky" },
                    top: { lg: stickyTop },
                    maxHeight: { lg: `calc(100vh - ${stickyTop + 16}px)` },
                    minHeight: 0,
                    minWidth: 0,
                    overflow: { lg: sidebarCollapsed ? "hidden" : "visible" },
                    visibility: { xs: "visible", lg: sidebarCollapsed ? "hidden" : "visible" },
                    transition: (theme) =>
                      theme.transitions.create(["top", "max-height"], {
                        duration: theme.transitions.duration.shorter
                      })
                  }}
                >
                  <Box sx={{ flexShrink: 0 }}>
                    <OrderSummaryCard
                      orderNumber={orderNumber}
                      orderInfo={orderInfo}
                      relatedOrders={relatedOrders}
                      isLoading={orderInfoLoading}
                      onOrderInfoUpdated={loadOrderInfo}
                    />
                  </Box>

                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 1,
                      borderColor: "#091A7A",
                      overflow: "hidden",
                      backgroundColor: "background.paper",
                      display: "flex",
                      flexDirection: "column",
                      flex: { xs: "0 0 auto", lg: "1 1 auto" },
                      minHeight: 0
                    }}
                  >
                    <Box sx={{ px: 2, py: 1.15, backgroundColor: "background.neutral", flexShrink: 0 }}>
                      <Typography variant="subtitle2" sx={{ color: "text.primary", fontWeight: 900 }}>
                        Categories
                      </Typography>
                    </Box>
                    <Divider />
                    <Box
                      sx={{
                        maxHeight: { xs: 280, lg: "none" },
                        overflowY: "auto",
                        overscrollBehaviorY: { lg: "contain" },
                        scrollbarGutter: { lg: "stable" },
                        minHeight: 0,
                        flex: { lg: "1 1 auto" },
                        p: 1
                      }}
                    >
                      <CategoryNavigator
                        categories={filteredCategories}
                        onSelect={scrollToSubcategory}
                        onSelectCategory={scrollToCategory}
                      />
                    </Box>
                  </Paper>
                </Stack>

                <Paper
                  variant="outlined"
                  data-testid="documents-workspace"
                  sx={{
                    borderRadius: 1,
                    borderColor: "#091A7A",
                    overflow: { xs: "hidden", lg: "visible" },
                    boxShadow: "0 8px 24px rgba(22, 28, 36, 0.04)"
                  }}
                >
                  <DocumentsActionBar
                    stickyRef={stickyHeaderRef}
                    orderNumber={orderNumber}
                    orderInfo={orderInfo}
                    orderNumberInput={orderNumberInput}
                    onOrderNumberInputChange={setOrderNumberInput}
                    onOpenOrder={openOrderInNewTab}
                    sidebarCollapsed={sidebarCollapsed}
                    onToggleSidebar={toggleSidebar}
                    documentViewMode={documentViewMode}
                    onDocumentViewModeChange={updateDocumentViewMode}
                    searchText={searchText}
                    onSearchTextChange={setSearchText}
                    onDocumentsUploaded={loadDocuments}
                    uploadOpen={uploadOpen}
                    uploadFile={uploadFile}
                    onOpenUpload={openUploadDrawer}
                    onCloseUpload={closeUploadDrawer}
                    onContactsChanged={async () => {
                      await Promise.all([loadContacts(), loadOrderInfo()]);
                    }}
                    contacts={contacts}
                    contactsLoading={contactsLoading}
                    contactsError={contactsError}
                    selectedDocuments={selectedDocuments}
                    allVisibleDocumentsSelected={allVisibleSelected}
                    onToggleAllVisibleDocuments={toggleAllVisibleDocuments}
                    onRemoveSelectedDocument={removeSelectedDocument}
                  />

                  {loading || (orderInfoPending && filteredCategories.length === 0) ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
                      <CircularProgress />
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        p: 1.5,
                        backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.025),
                        minHeight: filteredCategories.length === 0 ? 420 : "auto",
                        position: "relative"
                      }}
                    >
                      {filteredCategories.length === 0 ? (
                        <DocumentsNotFound>
                          {orderNotFound ? `Order ${orderNumber} not found.` : "There are no documents for this order."}
                        </DocumentsNotFound>
                      ) : documentViewMode === "date" ? (
                        <DateDocumentsSection
                          orderNumber={orderNumber}
                          emailSubjectLine={orderInfo?.emailSubjectLine}
                          documents={dateSortedDocuments}
                          selectedDocumentIds={selectedDocumentIds}
                          onToggleDocumentSelection={toggleDocumentSelection}
                          onToggleDocumentSelections={toggleDocumentSelections}
                          onDocumentsChanged={loadDocuments}
                        />
                      ) : (
                        <Stack spacing={1.5}>
                          {filteredCategories.map((category) => (
                            <CategorySection
                              key={category.name}
                              category={category}
                              sectionRefs={sectionRefs}
                              themeColor={theme.palette.primary.main}
                              orderNumber={orderNumber}
                              emailSubjectLine={orderInfo?.emailSubjectLine}
                              collapsedCategoryNames={collapsedCategoryNames}
                              onToggleCategory={toggleCategory}
                              selectedDocumentIds={selectedDocumentIds}
                              onToggleDocumentSelection={toggleDocumentSelection}
                              onToggleDocumentSelections={toggleDocumentSelections}
                              onDocumentsChanged={loadDocuments}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  )}
                </Paper>
              </Box>
            ))}
        </Stack>
      </Container>
    </>
  );
}

function getMobileTransactionTypeLabel(transactionType: string) {
  const normalized = transactionType.trim().toLowerCase();

  if (normalized === "purchase") {
    return "P";
  }

  if (normalized === "refinance" || normalized === "refi") {
    return "R";
  }

  if (normalized === "other") {
    return "O";
  }

  return transactionType;
}

function formatMobileCurrency(value?: number) {
  if (!value) {
    return "--";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(value);
}

function formatMobileDate(value?: string) {
  if (!value) {
    return "--";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  return format(date, "M/d/yyyy");
}

function MobileOrderDocumentsWorkspace({
  orderNumber,
  orderInfo,
  relatedOrders,
  orderInfoLoading,
  orderNumberInput,
  onOrderNumberInputChange,
  onOpenOrder,
  documentViewMode,
  onDocumentViewModeChange,
  searchText,
  onSearchTextChange,
  filteredCategories,
  dateSortedDocuments,
  loading,
  orderInfoPending,
  orderNotFound,
  selectedDocumentIds,
  selectedDocuments,
  allVisibleDocumentsSelected,
  onToggleAllVisibleDocuments,
  onToggleDocumentSelection,
  onRemoveSelectedDocument,
  onDocumentsUploaded,
  uploadOpen,
  uploadFile,
  onOpenUpload,
  onCloseUpload,
  onOrderInfoUpdated,
  onContactsChanged,
  contacts,
  contactsLoading,
  contactsError
}: {
  orderNumber: string;
  orderInfo: DocumentsOrderInfo | null;
  relatedOrders: string[];
  orderInfoLoading: boolean;
  orderNumberInput: string;
  onOrderNumberInputChange: (value: string) => void;
  onOpenOrder: (orderNumber: string) => void;
  documentViewMode: "category" | "date";
  onDocumentViewModeChange: (value: "category" | "date") => void;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  filteredCategories: OrderDocumentCategory[];
  dateSortedDocuments: OrderDocumentDetail[];
  loading: boolean;
  orderInfoPending: boolean;
  orderNotFound: boolean;
  selectedDocumentIds: Set<number>;
  selectedDocuments: OrderDocumentDetail[];
  allVisibleDocumentsSelected: boolean;
  onToggleAllVisibleDocuments: () => void;
  onToggleDocumentSelection: (documentId: number, selected: boolean) => void;
  onRemoveSelectedDocument: (documentId: number) => void;
  onDocumentsUploaded: () => Promise<void>;
  uploadOpen: boolean;
  uploadFile: File | null;
  onOpenUpload: () => void;
  onCloseUpload: () => void;
  onOrderInfoUpdated: () => Promise<void>;
  onContactsChanged: () => Promise<void>;
  contacts: DocumentsOrderContact[];
  contactsLoading: boolean;
  contactsError: string | null;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Merge & Print lives inside DocumentsActionBar (which owns the merge dialog
  // state), so the card exposes a slot for it to portal into.
  const [cardActionSlotEl, setCardActionSlotEl] = useState<HTMLElement | null>(null);
  const accountRepName = orderInfo?.accountRep?.fullName || orderInfo?.accountRep?.name;
  const accountRepEmail = orderInfo?.accountRep?.email;
  const accountRepSubject = orderInfo?.emailSubjectLine
    ? `?subject=${encodeURIComponent(orderInfo.emailSubjectLine)}`
    : "";
  const mobilePurchasePrice =
    orderInfo?.salesPrice && orderInfo.salesPrice !== 0
      ? formatMobileCurrency(orderInfo.salesPrice)
      : orderInfo?.loanAmounts?.length
        ? orderInfo.loanAmounts.map(formatMobileCurrency).join(" & ")
        : "--";
  const mobilePurchaseDate = formatMobileDate(orderInfo?.settlementDate || orderInfo?.commitmentDate);
  const mobileHeaderLoading = orderInfoLoading && !orderInfo;
  return (
    <Stack spacing={1.25} sx={{ pb: 10 }}>
      {/* Order lookup lives in the site header, pinned to the top-right corner. */}
      {/* Two bands: what this order *is* (status, type, rep) on top, and what you can
          *do* with the list below it (view toggle, bulk action) under a divider — so
          the controls share one baseline instead of floating at opposite corners. */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 1, borderColor: (theme) => alpha(theme.palette.grey[500], 0.2) }}
      >
        {/* Order number lives in the site header on mobile — not repeated here. */}
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ px: 1.5, py: 1.25 }}
        >
          {mobileHeaderLoading && (
            <>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="rounded" width={92} height={24} sx={{ borderRadius: 999 }} />
            </>
          )}
          {orderInfo?.transactionType && (
            <Chip
              label={getMobileTransactionTypeLabel(orderInfo.transactionType)}
              size="small"
              sx={{
                height: 24,
                minWidth: 24,
                border: 0,
                borderRadius: 999,
                color: "#0B1D91",
                backgroundColor: alpha("#0B1D91", 0.1),
                fontWeight: 700,
                "& .MuiChip-label": {
                  px: 0.75
                }
              }}
            />
          )}
          {!mobileHeaderLoading && orderInfo?.status && (
            // Same treatment as the desktop status pill: the status's own color on a
            // 12% tint of itself, keyed off the shared statusColorMap.
            <Chip
              label={orderInfo.status}
              size="small"
              sx={{
                height: 24,
                fontWeight: 600,
                color: statusColorMap[orderInfo.status] || statusColorMap.Default,
                backgroundColor: alpha(statusColorMap[orderInfo.status] || statusColorMap.Default, 0.12)
              }}
            />
          )}
          <Box
            sx={{
              ml: "auto",
              width: "auto",
              display: "grid",
              gridTemplateColumns: "auto auto",
              gap: 2
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "text.secondary", fontSize: 11, fontWeight: 600, lineHeight: 1.2 }}>
                Purchase Price
              </Typography>
              {mobileHeaderLoading ? (
                <Skeleton variant="text" width={88} height={22} />
              ) : (
                <Typography sx={{ color: "primary.dark", fontSize: 14, fontWeight: 700, lineHeight: 1.25 }}>
                  {mobilePurchasePrice}
                </Typography>
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "text.secondary", fontSize: 11, fontWeight: 600, lineHeight: 1.2 }}>
                Purchase Date
              </Typography>
              {mobileHeaderLoading ? (
                <Skeleton variant="text" width={78} height={22} />
              ) : (
                <Typography sx={{ color: "primary.dark", fontSize: 14, fontWeight: 700, lineHeight: 1.25 }}>
                  {mobilePurchaseDate}
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>

        <Divider sx={{ mx: 1.5 }} />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, py: 1.25 }}
        >
          <Box
            sx={{
              minWidth: 0,
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5
            }}
          >
            {/* flexShrink: 0 — this group holds fixed-size controls, so letting it
                shrink made Merge & Print overflow and sit under the rep's name. */}
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
              {/* Icons only on mobile — the desktop toolbar keeps the text labels. */}
              <SegmentedButtons
                iconOnly
                value={documentViewMode}
                options={[
                  {
                    value: "category",
                    label: "Category",
                    icon: <Iconify icon="eva:folder-outline" width={18} />
                  },
                  {
                    value: "date",
                    label: "Date",
                    icon: <Iconify icon="eva:calendar-outline" width={18} />
                  }
                ]}
                onChange={(value) => onDocumentViewModeChange(value as "category" | "date")}
              />
              {/* DocumentsActionBar portals its Merge & Print button in here. */}
              <Box
                ref={setCardActionSlotEl}
                sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center" }}
              />
            </Stack>
            {mobileHeaderLoading && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Skeleton variant="rounded" width={18} height={16} />
                <Skeleton variant="text" width={90} height={22} />
              </Stack>
            )}
            {accountRepName &&
              (accountRepEmail ? (
                <Link
                  href={`mailto:${accountRepEmail}${accountRepSubject}`}
                  underline="hover"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    // Takes whatever the fixed-width controls leave rather than a
                    // fixed %, so the name truncates against real available space.
                    flex: 1,
                    minWidth: 0,
                    color: "primary.dark",
                    fontSize: 13,
                    fontWeight: 600
                  }}
                >
                  <Iconify icon="eva:email-outline" width={18} sx={{ flexShrink: 0 }} />
                  <Box
                    component="span"
                    // minWidth: 0 is what lets a flex child shrink below its text
                    // width — without it the ellipsis never engages and it overflows.
                    sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {accountRepName}
                  </Box>
                </Link>
              ) : (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Iconify icon="eva:email-outline" width={18} sx={{ color: "primary.dark", flexShrink: 0 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "primary.dark",
                      fontSize: 13,
                      fontWeight: 600,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {accountRepName}
                  </Typography>
                </Stack>
              ))}
          </Box>
        </Stack>
      </Paper>

      <DocumentsActionBar
        mobile
        mobileCardSlotEl={cardActionSlotEl}
        categories={filteredCategories}
        onOpenDetails={() => setDetailsOpen(true)}
        orderNumber={orderNumber}
        orderInfo={orderInfo}
        orderNumberInput={orderNumberInput}
        onOrderNumberInputChange={onOrderNumberInputChange}
        onOpenOrder={onOpenOrder}
        sidebarCollapsed={false}
        onToggleSidebar={() => {}}
        documentViewMode={documentViewMode}
        onDocumentViewModeChange={onDocumentViewModeChange}
        searchText={searchText}
        onSearchTextChange={onSearchTextChange}
        onDocumentsUploaded={onDocumentsUploaded}
        uploadOpen={uploadOpen}
        uploadFile={uploadFile}
        onOpenUpload={onOpenUpload}
        onCloseUpload={onCloseUpload}
        onContactsChanged={onContactsChanged}
        contacts={contacts}
        contactsLoading={contactsLoading}
        contactsError={contactsError}
        selectedDocuments={selectedDocuments}
        allVisibleDocumentsSelected={allVisibleDocumentsSelected}
        onToggleAllVisibleDocuments={onToggleAllVisibleDocuments}
        onRemoveSelectedDocument={onRemoveSelectedDocument}
      />

      {loading || (orderInfoPending && filteredCategories.length === 0) ? (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 280 }}>
          <CircularProgress />
        </Stack>
      ) : filteredCategories.length === 0 ? (
        <Box sx={{ px: 2, py: 8, textAlign: "center" }}>
          <Typography color="text.secondary">
            {orderNotFound ? `Order ${orderNumber} not found.` : "There are no documents for this order."}
          </Typography>
        </Box>
      ) : (
        <MobileDocumentsSection
          categories={filteredCategories}
          dateDocuments={dateSortedDocuments}
          viewMode={documentViewMode}
          orderNumber={orderNumber}
          selectedDocumentIds={selectedDocumentIds}
          onToggleDocumentSelection={onToggleDocumentSelection}
        />
      )}

      <Drawer
        anchor="bottom"
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        // Matches the search page quick-view sheet: detached, fully rounded, and the
        // sheet itself acts as the card.
        PaperProps={{
          sx: {
            m: 2,
            width: "auto",
            borderRadius: 3,
            maxHeight: "82vh",
            px: 2,
            pb: 2
          }
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>Order Details</Typography>
          <IconButton size="small" onClick={() => setDetailsOpen(false)}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>
        <Divider sx={{ mx: -2 }} />
        <Box
          sx={{
            overflowY: "auto",
            pt: 0.5,
            // Cancel the sheet's px:2 so the card isn't double-padded; the card's own
            // px provides the inset, then keep 10px of extra breathing room at the sides.
            mx: -0.75,
            "& > .MuiPaper-root": { border: 0, boxShadow: "none" }
          }}
        >
          <OrderSummaryCard
            orderNumber={orderNumber}
            orderInfo={orderInfo}
            relatedOrders={relatedOrders}
            isLoading={orderInfoLoading}
            onOrderInfoUpdated={onOrderInfoUpdated}
            compactLoading
          />
        </Box>
      </Drawer>
    </Stack>
  );
}

function SearchLoadingDots() {
  return (
    <Stack direction="row" spacing={0.4} alignItems="center">
      {[0, 1, 2].map((dot) => (
        <Box
          key={dot}
          sx={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            backgroundColor: "primary.dark",
            animation: "searchDotPulse 1s ease-in-out infinite",
            animationDelay: `${dot * 0.16}s`,
            "@keyframes searchDotPulse": {
              "0%, 80%, 100%": { opacity: 0.25, transform: "scale(0.8)" },
              "40%": { opacity: 1, transform: "scale(1)" }
            }
          }}
        />
      ))}
    </Stack>
  );
}

function OrderNumberSearchField({
  mobile = false,
  value,
  onChange,
  onOpenOrder
}: {
  mobile?: boolean;
  value: string;
  onChange: (value: string) => void;
  onOpenOrder: (orderNumber: string) => void;
}) {
  const [options, setOptions] = useState<string[]>([]);
  const [notFound, setNotFound] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const debouncedValue = useDebounce(value.trim(), 400);
  const suggestRequestId = useRef(0);

  useEffect(() => {
    const fetchSuggestions = async () => {
      const requestId = ++suggestRequestId.current;
      if (!debouncedValue) {
        setOptions([]);
        return;
      }

      setIsSuggesting(true);
      try {
        const { data } = await axios.get<string[]>("/proxy/api/orders/FindRelatedOrdersByNumber", {
          params: { searchText: debouncedValue },
          headers: { "x-suppress-global-error": "true" }
        });
        if (requestId === suggestRequestId.current) {
          setOptions((data ?? []).slice(0, 3));
        }
      } catch {
        if (requestId === suggestRequestId.current) {
          setOptions([]);
        }
      } finally {
        if (requestId === suggestRequestId.current) {
          setIsSuggesting(false);
        }
      }
    };

    fetchSuggestions();
  }, [debouncedValue]);

  const submit = async () => {
    const query = value.trim();
    if (!query || isValidating) {
      return;
    }

    setIsValidating(true);
    try {
      const { data } = await axios.get<string[]>("/proxy/api/orders/FindRelatedOrdersByNumber", {
        params: { searchText: query },
        headers: { "x-suppress-global-error": "true" }
      });
      const exactOrder = (data ?? []).find((orderNumber) => orderNumber.toLowerCase() === query.toLowerCase());
      if (exactOrder) {
        onOpenOrder(exactOrder);
      } else {
        setNotFound(true);
      }
    } catch {
      // Search is unavailable — fall back to opening the order directly.
      onOpenOrder(query);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      filterOptions={(currentOptions) => currentOptions}
      getOptionLabel={(option) => option}
      inputValue={value}
      onInputChange={(_, nextValue) => {
        onChange(nextValue);
        setNotFound(false);
      }}
      onChange={(_, option) => {
        if (option) {
          onOpenOrder(option);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !event.defaultMuiPrevented) {
          submit();
        }
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {option}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="enter order # to open"
          error={notFound}
          helperText={notFound ? "Order not found" : ""}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-outline" sx={{ color: "primary.dark" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {(isSuggesting || isValidating) && (
                  <InputAdornment position="end">
                    <SearchLoadingDots />
                  </InputAdornment>
                )}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      sx={{
        // On mobile, absolutely positioned within its 32px wrapper, right-anchored
        // so expanding grows leftward and overlays the buttons before it instead of
        // pushing them (the wrapper's layout size never changes). Desktop stays
        // inline in its toolbar.
        ...(mobile && { position: "absolute", top: 0, right: 0, zIndex: 1 }),
        width: value ? 220 : 32,
        transition: (theme) => theme.transitions.create("width"),
        "&:hover, &:focus-within": { width: 220 },
        "& .MuiOutlinedInput-root": {
          borderRadius: 1,
          height: 32,
          fontSize: 13,
          py: 0,
          px: "5px",
          flexWrap: "nowrap",
          overflow: "hidden",
          backgroundColor: "background.paper",
          // Shadow lives on the rounded input (not the rectangular root) so it
          // follows the border radius instead of leaking past the corners.
          transition: (theme) => theme.transitions.create("box-shadow")
        },
        "&:hover .MuiOutlinedInput-root, &:focus-within .MuiOutlinedInput-root": {
          boxShadow: mobile ? 4 : "none"
        },
        "& .MuiOutlinedInput-input": {
          py: 0,
          px: 0.5,
          opacity: value ? 1 : 0,
          transition: (theme) => theme.transitions.create("opacity")
        },
        "&:hover .MuiOutlinedInput-input, &:focus-within .MuiOutlinedInput-input": {
          opacity: 1
        }
      }}
    />
  );
}

// Mobile header actions: 32px circle matching the collapsed search field's
// footprint, on a light grey fill so they read as buttons rather than bare glyphs.
const headerIconButtonSx = {
  width: 32,
  height: 32,
  p: 0,
  color: "primary.dark",
  backgroundColor: (theme: Theme) => alpha(theme.palette.grey[500], 0.12),
  "&:hover": {
    backgroundColor: (theme: Theme) => alpha(theme.palette.grey[500], 0.24)
  }
};

function DocumentsActionBar({
  mobile = false,
  mobileCardSlotEl = null,
  stickyRef,
  categories = [],
  onOpenDetails,
  orderNumber,
  orderInfo,
  orderNumberInput,
  onOrderNumberInputChange,
  onOpenOrder,
  sidebarCollapsed,
  onToggleSidebar,
  documentViewMode,
  onDocumentViewModeChange,
  searchText,
  onSearchTextChange,
  onDocumentsUploaded,
  uploadOpen,
  uploadFile,
  onOpenUpload,
  onCloseUpload,
  onContactsChanged,
  contacts,
  contactsLoading,
  contactsError,
  selectedDocuments,
  allVisibleDocumentsSelected,
  onToggleAllVisibleDocuments,
  onRemoveSelectedDocument
}: {
  mobile?: boolean;
  mobileCardSlotEl?: HTMLElement | null;
  stickyRef?: MutableRefObject<HTMLDivElement | null>;
  categories?: OrderDocumentCategory[];
  onOpenDetails?: () => void;
  orderNumber: string;
  orderInfo: DocumentsOrderInfo | null;
  orderNumberInput: string;
  onOrderNumberInputChange: (value: string) => void;
  onOpenOrder: (orderNumber: string) => void;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  documentViewMode: "category" | "date";
  onDocumentViewModeChange: (value: "category" | "date") => void;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  onDocumentsUploaded: () => Promise<void>;
  uploadOpen: boolean;
  uploadFile: File | null;
  onOpenUpload: () => void;
  onCloseUpload: () => void;
  onContactsChanged: () => Promise<void>;
  contacts: DocumentsOrderContact[];
  contactsLoading: boolean;
  contactsError: string | null;
  selectedDocuments: OrderDocumentDetail[];
  allVisibleDocumentsSelected: boolean;
  onToggleAllVisibleDocuments: () => void;
  onRemoveSelectedDocument: (documentId: number) => void;
}) {
  const { slotEl } = useContext(HeaderSlotContext);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [indexOpen, setIndexOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<SelectedDocumentAction | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const stickyTop = useStickyHeaderTop();
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [barcodeOptions, setBarcodeOptions] = useState<BarcodeOption[]>([]);
  const [selectedBarcodeDescriptions, setSelectedBarcodeDescriptions] = useState<string[]>([]);
  const [barcodesLoading, setBarcodesLoading] = useState(false);
  const [barcodesGenerating, setBarcodesGenerating] = useState(false);
  const [barcodeError, setBarcodeError] = useState("");
  const [teamsOpening, setTeamsOpening] = useState(false);
  const [noteToResolve, setNoteToResolve] = useState<DocumentsOrderNote | null>(null);
  const [resolveNote, setResolveNote] = useState("");
  const [resolveError, setResolveError] = useState("");
  const [isResolving, setIsResolving] = useState(false);

  // Close the index drawer, then smooth-scroll to the picked category/subcategory.
  const scrollToDomId = (domId: string) => {
    setIndexOpen(false);

    // The index lists categories, which only exist in the category view — in date
    // view the target element is never rendered, so switch back first.
    if (documentViewMode !== "category") {
      onDocumentViewModeChange("category");
    }

    // That switch remounts the list, so the anchor may not exist on the next tick.
    // Retry briefly rather than guessing a single render delay.
    let attempts = 0;
    const tryScroll = () => {
      const element = document.getElementById(domId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      attempts += 1;
      if (attempts < 20) {
        window.setTimeout(tryScroll, 50);
      }
    };

    window.setTimeout(tryScroll, 60);
  };

  const accountRepName = orderInfo?.accountRep?.fullName || orderInfo?.accountRep?.name;
  const accountRepEmail = orderInfo?.accountRep?.email;
  const accountRepLabel = accountRepName || "Account rep";
  const accountRepSubject = orderInfo?.emailSubjectLine ? encodeURIComponent(orderInfo.emailSubjectLine) : "";
  const importantNotes = orderInfo?.notes?.filter((note) => note.displayOnOrderOpen) ?? [];
  const allNotes = orderInfo?.notes?.filter((note) => note.includeInGeneralOrderNotes) ?? [];
  const canGenerateBarcodes = orderInfo?.stateCode?.toUpperCase() === "NY";
  const openInSelectHref = orderInfo?.identifier ? `sp-select://./ordertracking/order?id=${orderInfo.identifier}` : "";
  const shippingHref = orderInfo?.number
    ? `https://momentum-geshikt.azurewebsites.net/ship/${encodeURIComponent(orderInfo.number)}`
    : "";
  const teamsChannelName =
    orderInfo?.titleCode && orderInfo?.number
      ? `${orderInfo.titleCode}-${orderInfo.number}`
      : orderInfo?.number || orderNumber;

  const closeResolveDialog = () => {
    if (isResolving) {
      return;
    }

    setNoteToResolve(null);
    setResolveNote("");
    setResolveError("");
  };
  const handleResolveOrderNote = async () => {
    const trimmedNote = resolveNote.trim();
    if (!trimmedNote) {
      setResolveError("Please enter a note explaining why this was resolved.");
      return;
    }

    try {
      setIsResolving(true);
      setResolveError("");
      await axios.post("/api/orderdocuments/resolveblacklistordernote", {
        orderNumber,
        noteText: noteToResolve?.text,
        resolutionNote: trimmedNote
      });
      await onContactsChanged();
      setNoteToResolve(null);
      setResolveNote("");
      setResolveError("");
    } catch (error) {
      setResolveError(getAxiosErrorMessage(error, "Unable to resolve this alert. Please try again."));
    } finally {
      setIsResolving(false);
    }
  };
  const handleDownloadSelected = async () => {
    if (!selectedDocuments.length) {
      return;
    }

    setIsDownloading(true);
    try {
      const { data } = await axios.post(
        "/api/orderdocuments/downloadaszip",
        { documentIds: selectedDocuments.map((document) => document.id) },
        { responseType: "blob" }
      );
      const zipOrderNumber = orderInfo?.number || orderNumber;
      const zipFileName = orderInfo?.titleCode
        ? `${orderInfo.titleCode}-${zipOrderNumber} documents.zip`
        : `${zipOrderNumber} documents.zip`;
      downloadBlob(data, zipFileName);
    } finally {
      setIsDownloading(false);
    }
  };
  const loadBarcodeOptions = async () => {
    setBarcodesLoading(true);
    setBarcodeError("");

    try {
      const { data } = await axios.get<BarcodeOption[]>("/api/orderdocuments/barcodes/options");
      const options = data ?? [];
      setBarcodeOptions(options);
      setSelectedBarcodeDescriptions(options.map((option) => option.description));
    } catch (error) {
      setBarcodeError(getAxiosErrorMessage(error, "Barcode options could not be loaded."));
    } finally {
      setBarcodesLoading(false);
    }
  };

  const openBarcodeDialog = () => {
    setBarcodeError("");
    setBarcodeOpen(true);
    if (barcodeOptions.length > 0) {
      setSelectedBarcodeDescriptions(barcodeOptions.map((option) => option.description));
    } else {
      void loadBarcodeOptions();
    }
  };

  const closeBarcodeDialog = () => {
    if (!barcodesGenerating) {
      setBarcodeOpen(false);
      setBarcodeError("");
    }
  };

  const toggleBarcodeDescription = (description: string) => {
    setSelectedBarcodeDescriptions((current) =>
      current.includes(description)
        ? current.filter((selectedDescription) => selectedDescription !== description)
        : [...current, description]
    );
  };

  const generateBarcodes = async () => {
    if (!orderNumber || barcodesGenerating) {
      return;
    }

    if (selectedBarcodeDescriptions.length === 0) {
      window.alert("Please select at least one barcode category.");
      return;
    }

    const barcodeWindow = window.open("", "MomentumBarcodePrint", "width=900,height=1000");
    if (!barcodeWindow) {
      window.alert("Unable to generate barcodes for this order.");
      return;
    }

    barcodeWindow.opener = null;
    setBarcodesGenerating(true);
    setBarcodeError("");

    try {
      const { data } = await axios.post(
        `/api/orderdocuments/barcodes/${encodeURIComponent(orderNumber)}`,
        { descriptions: selectedBarcodeDescriptions },
        { responseType: "blob" }
      );
      const barcodeBlob = data instanceof Blob ? data : new Blob([data], { type: "application/pdf" });
      const barcodeUrl = URL.createObjectURL(barcodeBlob);
      barcodeWindow.location.href = barcodeUrl;
      setTimeout(() => URL.revokeObjectURL(barcodeUrl), 60000);
      setBarcodeOpen(false);
    } catch (error) {
      barcodeWindow.close();
      const message = getAxiosErrorMessage(error, "Unable to generate barcodes for this order.");
      setBarcodeError(message);
      window.alert(message);
    } finally {
      setBarcodesGenerating(false);
    }
  };

  const openTeamsChannel = async () => {
    if (!orderNumber || teamsOpening) {
      return;
    }

    const teamsWindow = window.open("", "TitleQOrderTeamsChannel", "width=1100,height=800");
    if (!teamsWindow) {
      window.alert("Unable to open Microsoft Teams. Please allow popups and try again.");
      return;
    }

    teamsWindow.opener = null;
    writeTeamsLoadingPage(teamsWindow, teamsChannelName);
    setTeamsOpening(true);

    try {
      const { data } = await axios.post<TeamsChannelResult>("/api/orderdocuments/teams/channel", {
        orderNumber,
        channelName: teamsChannelName
      });

      if (!data?.webUrl) {
        throw new Error("Teams did not return a channel link.");
      }

      teamsWindow.location.href = data.webUrl;
    } catch (error) {
      teamsWindow.close();
      window.alert(getAxiosErrorMessage(error, "Unable to open or create the Teams channel for this order."));
    } finally {
      setTeamsOpening(false);
    }
  };

  return (
    <>
      <Dialog open={Boolean(noteToResolve)} onClose={closeResolveDialog} fullWidth maxWidth="sm">
        <DialogTitle>Resolve blacklist/patriot note</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Typography variant="body2">
            Please enter an explanation for resolving this blacklist/patriot alert.
          </Typography>
          {resolveError && <Alert severity="error">{resolveError}</Alert>}
          <TextField
            size="small"
            label="Explanation"
            multiline
            minRows={4}
            value={resolveNote}
            onChange={(event) => setResolveNote(event.target.value)}
            disabled={isResolving}
            autoFocus
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={closeResolveDialog} disabled={isResolving}>
            Cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={handleResolveOrderNote}
            disabled={isResolving}
          >
            {isResolving ? "Resolving..." : "Resolve"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={barcodeOpen} onClose={closeBarcodeDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          <Typography variant="h6" sx={{ color: "primary.dark", fontWeight: 700 }}>
            Generate Barcodes
          </Typography>
          <IconButton size="small" onClick={closeBarcodeDialog} disabled={barcodesGenerating}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            {barcodeError && <Alert severity="error">{barcodeError}</Alert>}
            {barcodesLoading ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading barcode categories...
                </Typography>
              </Stack>
            ) : (
              barcodeOptions.map((option) => (
                <FormControlLabel
                  key={option.description}
                  control={
                    <Checkbox
                      checked={selectedBarcodeDescriptions.includes(option.description)}
                      onChange={() => toggleBarcodeDescription(option.description)}
                    />
                  }
                  label={option.description}
                  sx={{
                    m: 0,
                    px: 1.25,
                    py: 0.75,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    backgroundColor: "background.paper"
                  }}
                />
              ))
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button size="small" onClick={closeBarcodeDialog} disabled={barcodesGenerating}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={generateBarcodes}
            disabled={barcodesLoading || barcodesGenerating || selectedBarcodeDescriptions.length === 0}
          >
            {barcodesGenerating ? "Generating..." : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>
      {mobile &&
        slotEl &&
        createPortal(
          // Current order's number pinned top-left next to the nav button. Notes,
          // Contacts, and the search-another-order field stay pinned top-right; the
          // search field is absolutely positioned within its fixed-size wrapper so
          // expanding it overlays the buttons to its left instead of pushing them.
          <Stack direction="row" alignItems="center" sx={{ width: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "primary.dark",
                fontSize: 18,
                fontWeight: 500,
                minWidth: 0,
                flexShrink: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {orderInfo?.titleCode
                ? `${orderInfo.titleCode}-${orderInfo?.number || orderNumber}`
                : orderInfo?.number || orderNumber}
            </Typography>
            {/* All three share a 32px box and a 20px glyph so the gaps between them
                read as even — the search field's collapsed state sets that footprint. */}
            <Stack direction="row" spacing={1.75} alignItems="center" sx={{ ml: "auto", flexShrink: 0 }}>
              <IconButton
                aria-label="Order history"
                onClick={() => setHistoryOpen(true)}
                sx={{ ...headerIconButtonSx }}
              >
                <Iconify icon="mdi:history" width={20} />
              </IconButton>
              <IconButton
                aria-label="Notes"
                onClick={() => setNotesOpen(true)}
                sx={{ ...headerIconButtonSx }}
              >
                <Iconify icon="eva:file-text-outline" width={20} />
              </IconButton>
              <IconButton
                aria-label="Contacts"
                onClick={() => setContactsOpen(true)}
                sx={{ ...headerIconButtonSx }}
              >
                <Iconify icon="eva:people-outline" width={20} />
              </IconButton>
              <Box sx={{ position: "relative", width: 32, height: 32, flexShrink: 0 }}>
                <OrderNumberSearchField
                  mobile
                  value={orderNumberInput}
                  onChange={onOrderNumberInputChange}
                  onOpenOrder={onOpenOrder}
                />
              </Box>
            </Stack>
          </Stack>,
          slotEl
        )}

      {mobile &&
        mobileCardSlotEl &&
        createPortal(
          <Button
            size="small"
            variant="contained"
            disabled={selectedDocuments.length === 0}
            onClick={() => {
              setEmailOpen(false);
              setSelectedAction("mergePrint");
            }}
            sx={{ whiteSpace: "nowrap", fontSize: 12 }}
          >
            Merge &amp; Print
            {selectedDocuments.length > 0 ? ` (${selectedDocuments.length})` : ""}
          </Button>,
          mobileCardSlotEl
        )}

      {mobile ? null : (
        <Box
          ref={stickyRef}
          sx={{
            position: { lg: "sticky" },
            top: { lg: stickyTop },
            zIndex: 3,
            backgroundColor: "background.paper",
            borderRadius: { lg: "8px 8px 0 0" },
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            transition: (theme) =>
              theme.transitions.create("top", {
                duration: theme.transitions.duration.shorter
              }),
            // Rows scrolling under this sticky bar are otherwise visible in the strip
            // between it and the app header (which is translucent). Paint everything
            // above the bar, stopping 1px short so the card's top border stays visible.
            "&::before": {
              content: '""',
              position: "absolute",
              left: -1,
              right: -1,
              bottom: "calc(100% + 1px)",
              height: "100vh",
              backgroundColor: "background.default",
              display: { xs: "none", lg: "block" }
            }
          }}
        >
          {importantNotes.length > 0 && (
            <Box sx={{ p: 1.25 }}>
              <Box
                sx={{
                  backgroundColor: "#ee765c",
                  px: 3,
                  borderRadius: 1.5,
                  // Clip the first/last note rows to the rounded corners.
                  overflow: "hidden"
                }}
              >
                {importantNotes.map((note, index) => (
                  <Box
                    key={`${note.createdOn}-${index}`}
                    sx={{
                      color: "common.white",
                      py: 1,
                      px: 2,
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      lineHeight: 1.35,
                      textAlign: "center",
                      fontWeight: 700,
                      borderBottom: index === importantNotes.length - 1 ? "none" : "1px solid #ff9b86"
                    }}
                  >
                    <Box component="span" sx={{ overflowWrap: "anywhere" }}>
                      {note.text?.trimEnd()}
                    </Box>
                    {isResolvableOrderAlertNote(note) && (
                      <Button
                        color="error"
                        size="small"
                        variant="contained"
                        onClick={() => setNoteToResolve(note)}
                        sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
                      >
                        Resolve
                      </Button>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Tooltip title={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}>
            <IconButton
              size="small"
              onClick={onToggleSidebar}
              sx={{
                display: { xs: "none", lg: "inline-flex" },
                position: "absolute",
                // Expanded: centre the 32px button on the sidebar card's right edge,
                // which sits one full grid gap (24px) to the left of this card.
                left: sidebarCollapsed ? -16 : -40,
                top: 24,
                zIndex: 4,
                width: 32,
                height: 32,
                color: "primary.dark",
                backgroundColor: "background.paper",
                border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.32)}`,
                boxShadow: "0 2px 6px rgba(22, 28, 36, 0.12)",
                "&:hover": {
                  backgroundColor: "background.neutral"
                }
              }}
            >
              <Iconify
                icon={sidebarCollapsed ? "eva:chevron-right-fill" : "eva:chevron-left-fill"}
                width={32}
                sx={{ flexShrink: 0 }}
              />
            </IconButton>
          </Tooltip>
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "1fr auto 1fr" },
              gap: 1.5,
              alignItems: "center"
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
              {accountRepEmail ? (
                <Link
                  href={`mailto:${accountRepEmail}${accountRepSubject ? `?subject=${accountRepSubject}` : ""}`}
                  underline="hover"
                  sx={{ color: "primary.dark", fontWeight: 900 }}
                >
                  {accountRepLabel}
                </Link>
              ) : (
                <Typography variant="subtitle2" sx={{ color: "primary.dark", fontWeight: 900 }}>
                  {accountRepLabel}
                </Typography>
              )}
              <OrderNumberSearchField
                value={orderNumberInput}
                onChange={onOrderNumberInputChange}
                onOpenOrder={onOpenOrder}
              />
            </Stack>

            <Box sx={{ display: { xs: "none", xl: "block" } }} />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent={{ xs: "flex-start", xl: "flex-end" }}
            >
              <SegmentedButtons
                value={documentViewMode}
                options={[
                  { value: "category", label: "Category" },
                  { value: "date", label: "Date" }
                ]}
                onChange={(value) => onDocumentViewModeChange(value as "category" | "date")}
              />

              <TextField
                size="small"
                placeholder="Search Documents"
                value={searchText}
                onChange={(event) => onSearchTextChange(event.target.value)}
                sx={{
                  minWidth: { sm: 180, lg: 220 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    height: 32,
                    fontSize: 13
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" />
                    </InputAdornment>
                  ),
                  endAdornment: searchText ? (
                    <InputAdornment position="end">
                      <Tooltip title="Clear search">
                        <IconButton
                          aria-label="Clear document search"
                          edge="end"
                          size="small"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => onSearchTextChange("")}
                          sx={{ color: "text.secondary" }}
                        >
                          <Iconify icon="eva:close-fill" width={16} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Stack>
          </Box>

          <Box
            sx={{
              px: 2,
              pb: 1,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "1fr auto 1fr" },
              gap: 1.5,
              alignItems: "center"
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <ActionButtonGroup
                actions={[
                  {
                    label: allVisibleDocumentsSelected ? "Uncheck All" : "Check All",
                    onClick: onToggleAllVisibleDocuments
                  },
                  { label: "Contacts", onClick: () => setContactsOpen(true) },
                  { label: "Notes", onClick: () => setNotesOpen(true) },
                  { label: "History", onClick: () => setHistoryOpen(true) },
                  { label: "Open in Select", href: openInSelectHref, disabled: !openInSelectHref },
                  { label: "Shipping", href: shippingHref, target: "_blank", disabled: !shippingHref },
                  { label: "Upload", onClick: onOpenUpload },
                  {
                    label: teamsOpening ? "Teams..." : "Teams",
                    icon: <Iconify icon="logos:microsoft-teams" width={15} />,
                    onClick: openTeamsChannel,
                    disabled: teamsOpening
                  },
                  ...(canGenerateBarcodes ? [{ label: "Barcodes", onClick: openBarcodeDialog }] : [])
                ]}
              />
              <DocumentAiReview orderNumber={orderNumber} selectedDocuments={selectedDocuments} />
            </Stack>
            <Box />
            <ActionButtonGroup
              actions={[
                {
                  label: "Email",
                  onClick: () => {
                    setSelectedAction(null);
                    setEmailOpen(true);
                  }
                },
                {
                  label: "Merge & Print",
                  onClick: () => {
                    setEmailOpen(false);
                    setSelectedAction("mergePrint");
                  }
                },
                {
                  label: "Merge",
                  onClick: () => {
                    setEmailOpen(false);
                    setSelectedAction("merge");
                  }
                },
                {
                  label: "Copy",
                  onClick: () => {
                    setEmailOpen(false);
                    setSelectedAction("copy");
                  }
                },
                {
                  label: "Delete",
                  onClick: () => {
                    setEmailOpen(false);
                    setSelectedAction("delete");
                  },
                  disabled: selectedDocuments.length === 0
                },
                {
                  label: isDownloading ? "Downloading" : "Download",
                  onClick: handleDownloadSelected,
                  disabled: selectedDocuments.length === 0 || isDownloading
                }
              ]}
              align="right"
            />
          </Box>
        </Box>
      )}

      {mobile && (
        // Floats above the selection bar, over the document list.
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: "calc(12px + env(safe-area-inset-bottom))",
            zIndex: (theme) => theme.zIndex.drawer - 1,
            px: 1.5,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none"
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            // No panel behind the controls: a tinted, blurred container plus the
            // white pill inside it read as two stacked layers. Each control carries
            // its own shadow instead, so they float directly over the list.
            sx={{ pointerEvents: "auto", minWidth: 0 }}
          >
            {categories.length > 0 && (
              <IconButton
                aria-label="Category index"
                onClick={() => setIndexOpen(true)}
                sx={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  color: "primary.dark",
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: (theme) => alpha(theme.palette.grey[500], 0.48),
                  boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)",
                  "&:hover": { backgroundColor: "background.paper" }
                }}
              >
                <Iconify icon="mdi:format-list-bulleted-square" width={22} />
              </IconButton>
            )}
            <TextField
              size="small"
              placeholder="Search documents"
              value={searchText}
              onChange={(event) => onSearchTextChange(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <Tooltip title="Clear search">
                      <IconButton
                        aria-label="Clear document search"
                        edge="end"
                        size="small"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => onSearchTextChange("")}
                        sx={{ color: "text.secondary" }}
                      >
                        <Iconify icon="eva:close-fill" width={16} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null
              }}
              sx={{
                // Deliberately narrower than a document row, with a hard edge and a deeper
                // shadow, so it reads as an overlay rather than one more row in the list.
                width: "min(100%, 320px)",
                borderRadius: 999,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 999,
                  backgroundColor: "background.paper",
                  // On the painted pill, not the transparent wrapper — otherwise the
                  // shadow traces a box that doesn't match the visible shape.
                  boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)"
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => alpha(theme.palette.grey[500], 0.48)
                }
              }}
            />
            {onOpenDetails && (
              <IconButton
                aria-label="Order details"
                onClick={onOpenDetails}
                sx={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  color: "primary.dark",
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: (theme) => alpha(theme.palette.grey[500], 0.48),
                  boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)",
                  "&:hover": { backgroundColor: "background.paper" }
                }}
              >
                <Iconify icon="mdi:information-variant" width={28} />
              </IconButton>
            )}
          </Stack>
        </Box>
      )}

      {mobile && (
        <Drawer
          anchor="bottom"
          open={indexOpen}
          onClose={() => setIndexOpen(false)}
          PaperProps={{
            sx: { m: 2, width: "auto", borderRadius: 3, maxHeight: "70vh", px: 2, pb: 2 }
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 2, pb: 1 }}>
            <Typography sx={{ fontWeight: 700 }}>Jump to category</Typography>
            <IconButton size="small" onClick={() => setIndexOpen(false)}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Stack>
          <Divider sx={{ mx: -2 }} />
          <Box sx={{ overflowY: "auto", pt: 1 }}>
            <CategoryNavigator
              categories={categories}
              onSelectCategory={(categoryName) => scrollToDomId(getMobileCategoryDomId(categoryName))}
              onSelect={(categoryName, subcategoryId) =>
                scrollToDomId(getMobileSubcategoryDomId(categoryName, subcategoryId))
              }
            />
          </Box>
        </Drawer>
      )}

      {/* Selection bar hidden for now. */}
      {false && mobile && (
        <Paper
          elevation={12}
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (theme) => theme.zIndex.drawer - 1,
            px: 1.5,
            pt: 1,
            pb: "calc(8px + env(safe-area-inset-bottom))",
            borderRadius: "12px 12px 0 0"
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: "primary.dark", fontWeight: 900 }}>
                {selectedDocuments.length} selected
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setEmailOpen(false);
                setSelectedAction("mergePrint");
              }}
              sx={{ fontSize: 12 }}
            >
              Merge &amp; Print
            </Button>
          </Stack>
        </Paper>
      )}

      <ContactsDrawer
        open={contactsOpen}
        orderNumber={orderNumber}
        orderInfo={orderInfo || undefined}
        emailSubjectLine={orderInfo?.emailSubjectLine}
        contacts={contacts}
        loading={contactsLoading}
        error={contactsError}
        onContactsChanged={onContactsChanged}
        onClose={() => setContactsOpen(false)}
      />
      <NotesDrawer open={notesOpen} orderNumber={orderNumber} notes={allNotes} onClose={() => setNotesOpen(false)} />
      <OrderHistoryDrawer open={historyOpen} orderNumber={orderNumber} onClose={() => setHistoryOpen(false)} />
      <UploadDrawer
        open={uploadOpen}
        orderNumber={orderNumber}
        initialFile={uploadFile}
        onUploaded={onDocumentsUploaded}
        onClose={onCloseUpload}
      />
      <EmailDrawer
        open={emailOpen}
        orderNumber={orderNumber}
        orderInfo={orderInfo}
        contacts={contacts}
        contactsLoading={contactsLoading}
        selectedDocuments={selectedDocuments}
        onRemoveSelectedDocument={onRemoveSelectedDocument}
        onClose={() => setEmailOpen(false)}
      />
      <SelectedDocumentsActionDialog
        action={selectedAction}
        orderNumber={orderNumber}
        orderInfo={orderInfo}
        selectedDocuments={selectedDocuments}
        onRemoveSelectedDocument={onRemoveSelectedDocument}
        onDocumentsChanged={onDocumentsUploaded}
        onClose={() => setSelectedAction(null)}
      />
    </>
  );
}

type SelectedDocumentAction = "mergePrint" | "merge" | "copy" | "delete";

function SelectedDocumentsActionDialog({
  action,
  orderNumber,
  orderInfo,
  selectedDocuments,
  onRemoveSelectedDocument,
  onDocumentsChanged,
  onClose
}: {
  action: SelectedDocumentAction | null;
  orderNumber: string;
  orderInfo: DocumentsOrderInfo | null;
  selectedDocuments: OrderDocumentDetail[];
  onRemoveSelectedDocument: (documentId: number) => void;
  onDocumentsChanged: () => Promise<void>;
  onClose: () => void;
}) {
  const [modalDocuments, setModalDocuments] = useState<OrderDocumentDetail[]>([]);
  const [sortNewestFirst, setSortNewestFirst] = useState(true);
  const [categoryOptions, setCategoryOptions] = useState<OrderDocumentCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [copyDestinationOrders, setCopyDestinationOrders] = useState<string[]>([]);
  const [mergeName, setMergeName] = useState("");
  const [destinationOrders, setDestinationOrders] = useState("");
  const [copyWeb, setCopyWeb] = useState(false);
  const [deleteAfterMerge, setDeleteAfterMerge] = useState(false);
  const [saveToWeb, setSaveToWeb] = useState(false);
  const selectedDocumentsRef = useRef(selectedDocuments);
  selectedDocumentsRef.current = selectedDocuments;
  const [mergeData, setMergeData] = useState({
    mtaNum: orderNumber,
    textToLink: "",
    category: "",
    subcategoryId: "",
    subcategory: "",
    description: "",
    additionalDescription: ""
  });
  const open = Boolean(action);
  const actionTitle = getSelectedActionTitle(action);
  const selectedCategory = categoryOptions.find((category) => category.name === mergeData.category);
  const selectedSubcategory = categoryOptions
    .flatMap((category) => category.subcategories)
    .find((subcategory) => subcategory.id === mergeData.subcategoryId);
  const descriptionOptions = selectedSubcategory?.descriptions?.filter(Boolean) ?? [];
  const quickSearchItems = useMemo(
    () =>
      categoryOptions.flatMap((category) =>
        category.subcategories.flatMap((subcategory) =>
          (subcategory.descriptions ?? []).filter(Boolean).map((description) => ({
            label: `${category.name} > ${subcategory.name} > ${description}`,
            category: category.name,
            subcategory: subcategory.name,
            subcategoryId: subcategory.id,
            description
          }))
        )
      ),
    [categoryOptions]
  );
  const selectedQuickSearchItem =
    quickSearchItems.find(
      (item) =>
        item.category === mergeData.category &&
        item.subcategoryId === mergeData.subcategoryId &&
        item.description === mergeData.description
    ) ?? null;
  const mergeErrors = {
    mtaNum: submitClicked && !mergeData.mtaNum.trim(),
    category: submitClicked && !mergeData.category,
    subcategoryId: submitClicked && !mergeData.subcategoryId,
    description: submitClicked && !mergeData.description,
    additionalDescription: submitClicked && mergeData.description === "Other" && !mergeData.additionalDescription.trim()
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    setModalDocuments(selectedDocumentsRef.current);
    setSortNewestFirst(true);
    setError("");
    setSubmitClicked(false);
    setDestinationOrders("");
    setCopyWeb(false);
    setDeleteAfterMerge(false);
    setSaveToWeb(false);
    setCopyDestinationOrders([]);
    setMergeName(
      `${
        orderInfo?.titleCode
          ? `${orderInfo.titleCode}-${orderInfo?.number || orderNumber}`
          : orderInfo?.number || orderNumber
      } Document`
    );
    setMergeData({
      mtaNum: orderNumber,
      textToLink: "",
      category: "",
      subcategoryId: "",
      subcategory: "",
      description: "",
      additionalDescription: ""
    });
  }, [action, open, orderInfo?.titleCode, orderInfo?.number, orderNumber]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setModalDocuments((current) => {
      const selectedById = new Map(selectedDocuments.map((document) => [document.id, document]));
      const synchronized = current
        .filter((document) => selectedById.has(document.id))
        .map((document) => selectedById.get(document.id)!);
      const synchronizedIds = new Set(synchronized.map((document) => document.id));

      return [...synchronized, ...selectedDocuments.filter((document) => !synchronizedIds.has(document.id))];
    });
  }, [open, selectedDocuments]);

  useEffect(() => {
    if (action !== "merge" || categoryOptions.length) {
      return;
    }

    setCategoriesLoading(true);
    axios
      .get<OrderDocumentCategory[]>("/api/orderdocuments/categories", { params: { orderNumber } })
      .then(({ data }) => setCategoryOptions(sortDocumentCategories(data ?? [])))
      .catch(() => setCategoryOptions([]))
      .finally(() => setCategoriesLoading(false));
  }, [action, categoryOptions.length, orderNumber]);

  const removeDocument = (documentId: number) => {
    setModalDocuments((current) => current.filter((document) => document.id !== documentId));
    onRemoveSelectedDocument(documentId);
  };
  const clearModalDocuments = () => {
    modalDocuments.forEach((document) => onRemoveSelectedDocument(document.id));
    setModalDocuments([]);
  };
  const toggleSortModalDocuments = () => {
    const nextNewestFirst = !sortNewestFirst;
    setSortNewestFirst(nextNewestFirst);
    setModalDocuments((current) =>
      [...current].sort(
        (first, second) =>
          (new Date(second.date1).getTime() - new Date(first.date1).getTime()) * (nextNewestFirst ? 1 : -1)
      )
    );
  };
  const reorderDocuments = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    setModalDocuments((current) => {
      const next = [...current];
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination!.index, 0, moved);
      return next;
    });
  };

  const runAction = async (callback: () => Promise<void>) => {
    setIsSubmitting(true);
    setError("");
    try {
      await callback();
    } catch (exception) {
      setError(
        axios.isAxiosError(exception)
          ? exception.response?.data?.message ||
              String(exception.response?.data || "The selected document action failed.")
          : "The selected document action failed."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const mergePayload = () => ({
    documents: modalDocuments.map((document) => ({ sourceId: document.id }))
  });

  const handleMergePrint = () =>
    runAction(async () => {
      const { data } = await axios.post<{ mergedFile: string }>("/api/orderdocuments/merge", mergePayload());
      downloadBase64File(data.mergedFile, `${mergeName.replace(/\.pdf$/i, "") || "Documents"}.pdf`, "application/pdf");
      onClose();
    });

  const handleMergeToWeb = () =>
    runAction(async () => {
      setSubmitClicked(true);
      const hasErrors =
        !mergeData.mtaNum.trim() ||
        !mergeData.category ||
        !mergeData.subcategoryId ||
        !mergeData.description ||
        (mergeData.description === "Other" && !mergeData.additionalDescription.trim());
      if (hasErrors) {
        return;
      }

      await axios.post("/api/orderdocuments/mergeandsave", {
        ...mergePayload(),
        saveInfo: {
          mtaNum: mergeData.mtaNum,
          description: buildDocumentDescriptionFromForm(mergeData),
          textToLink: mergeData.textToLink,
          subcategoryId: mergeData.subcategoryId,
          web: saveToWeb,
          deleteOriginalDocuments: deleteAfterMerge
        }
      });
      await onDocumentsChanged();
      onClose();
    });

  const handleCopy = () =>
    runAction(async () => {
      const destinationOrderNumbers = destinationOrders
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
      if (!destinationOrderNumbers.length) {
        setError("Enter at least one destination order number.");
        return;
      }

      await axios.post("/api/orderdocuments/copy", {
        sourceIds: modalDocuments.map((document) => document.id),
        destinationOrderNumbers,
        copyWeb
      });
      setSuccessMessage("Documents copied.");
      setCopyDestinationOrders(destinationOrderNumbers);
      onClose();
    });

  const handleDelete = () =>
    runAction(async () => {
      await axios.post(
        "/api/orderdocuments/deletemultiple",
        modalDocuments.map((document) => document.id)
      );
      await onDocumentsChanged();
      onClose();
    });

  const submit = () => {
    if (action === "mergePrint") {
      handleMergePrint();
    } else if (action === "merge") {
      handleMergeToWeb();
    } else if (action === "copy") {
      handleCopy();
    } else if (action === "delete") {
      handleDelete();
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open && action !== "delete"}
        onClose={() => {
          if (!isSubmitting) {
            onClose();
          }
        }}
        PaperProps={{
          sx: {
            width: {
              xs: "100%",
              md: `min(${action === "merge" ? 860 : 620}px, calc(100vw - 360px))`
            },
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column"
          }
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider"
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {actionTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {orderNumber}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={isSubmitting}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ pt: 3, flex: 1 }}>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {action === "mergePrint" && (
              <TextField
                size="small"
                autoFocus
                label="Merged Name"
                value={mergeName}
                onChange={(event) => setMergeName(event.target.value.replace(/\.pdf$/i, ""))}
                fullWidth
              />
            )}

            {action === "merge" && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                  gap: 2
                }}
              >
                <Stack spacing={2}>
                  <TextField
                    size="small"
                    label="MTA#"
                    value={mergeData.mtaNum}
                    error={mergeErrors.mtaNum}
                    onChange={(event) => setMergeData({ ...mergeData, mtaNum: event.target.value })}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Text to link"
                    value={mergeData.textToLink}
                    onChange={(event) => setMergeData({ ...mergeData, textToLink: event.target.value })}
                    fullWidth
                  />
                  <Autocomplete
                    options={quickSearchItems}
                    value={selectedQuickSearchItem}
                    loading={categoriesLoading}
                    getOptionLabel={(option) => option.label}
                    onChange={(_, value) => {
                      if (!value) {
                        return;
                      }

                      setMergeData({
                        ...mergeData,
                        category: value.category,
                        subcategoryId: value.subcategoryId,
                        subcategory: value.subcategory,
                        description: value.description
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="QuickSearch of Category > Subcategory > Description"
                        placeholder="Select..."
                        error={mergeErrors.category || mergeErrors.subcategoryId || mergeErrors.description}
                      />
                    )}
                  />
                  <TextField
                    size="small"
                    select
                    label="Category"
                    value={mergeData.category}
                    error={mergeErrors.category}
                    disabled={categoriesLoading}
                    onChange={(event) =>
                      setMergeData({
                        ...mergeData,
                        category: event.target.value,
                        subcategoryId: "",
                        subcategory: "",
                        description: ""
                      })
                    }
                  >
                    {categoryOptions.map((category) => (
                      <MenuItem key={category.name} value={category.name}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    select
                    label="Subcategory"
                    value={mergeData.subcategoryId}
                    error={mergeErrors.subcategoryId}
                    disabled={!selectedCategory || categoriesLoading}
                    onChange={(event) => {
                      const subcategory = selectedCategory?.subcategories.find(
                        (option) => option.id === event.target.value
                      );
                      setMergeData({
                        ...mergeData,
                        subcategoryId: event.target.value,
                        subcategory: subcategory?.name ?? "",
                        description: ""
                      });
                    }}
                  >
                    {(selectedCategory?.subcategories ?? []).map((subcategory) => (
                      <MenuItem key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    select
                    label="Description"
                    value={mergeData.description}
                    error={mergeErrors.description}
                    disabled={!selectedSubcategory || categoriesLoading}
                    onChange={(event) => setMergeData({ ...mergeData, description: event.target.value })}
                  >
                    {[...descriptionOptions, "Other"].map((description) => (
                      <MenuItem key={description} value={description}>
                        {description}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label="Additional Description"
                    value={mergeData.additionalDescription}
                    error={mergeErrors.additionalDescription}
                    onChange={(event) => setMergeData({ ...mergeData, additionalDescription: event.target.value })}
                    fullWidth
                  />
                  <FormControlLabel
                    control={
                      <Switch checked={deleteAfterMerge} onChange={() => setDeleteAfterMerge((current) => !current)} />
                    }
                    label="Delete documents after merge"
                  />
                  <FormControlLabel
                    control={<Switch checked={saveToWeb} onChange={() => setSaveToWeb((current) => !current)} />}
                    label="Public"
                  />
                </Stack>
                <SelectedDocumentsList
                  documents={modalDocuments}
                  onRemove={removeDocument}
                  onReorder={reorderDocuments}
                  onClear={clearModalDocuments}
                  onToggleSort={toggleSortModalDocuments}
                  sortNewestFirst={sortNewestFirst}
                />
              </Box>
            )}

            {action === "copy" && (
              <>
                <TextField
                  size="small"
                  autoFocus
                  label="Destination order numbers"
                  placeholder="Separate multiple order numbers with commas"
                  value={destinationOrders}
                  onChange={(event) => setDestinationOrders(event.target.value)}
                  fullWidth
                />
                <FormControlLabel
                  control={<Switch checked={copyWeb} onChange={() => setCopyWeb((current) => !current)} />}
                  label="Copy public documents as public"
                />
              </>
            )}

            {action !== "merge" && (
              <SelectedDocumentsList
                documents={modalDocuments}
                onRemove={removeDocument}
                onReorder={reorderDocuments}
                onClear={clearModalDocuments}
                onToggleSort={toggleSortModalDocuments}
                sortNewestFirst={sortNewestFirst}
              />
            )}

            {action === "delete" && (
              <Alert severity="warning">Are you sure you want to delete the selected documents?</Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color={action === "delete" ? "error" : "primary"}
            onClick={submit}
            disabled={isSubmitting || modalDocuments.length === 0}
          >
            {isSubmitting ? "Working" : actionTitle}
          </Button>
        </DialogActions>
      </Drawer>
      <Dialog open={action === "delete"} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{actionTitle}</DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <SelectedDocumentsList
              documents={modalDocuments}
              onRemove={removeDocument}
              onReorder={reorderDocuments}
              onClear={clearModalDocuments}
              onToggleSort={toggleSortModalDocuments}
              sortNewestFirst={sortNewestFirst}
            />
            <Alert severity="warning">Are you sure you want to delete the selected documents?</Alert>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={submit}
            disabled={isSubmitting || modalDocuments.length === 0}
          >
            {isSubmitting ? "Working" : actionTitle}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(successMessage)} autoHideDuration={3500} onClose={() => setSuccessMessage("")}>
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          <Stack spacing={0.5}>
            <Typography variant="body2">{successMessage}</Typography>
            {copyDestinationOrders.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {copyDestinationOrders.map((destinationOrder) => (
                  <Link
                    key={destinationOrder}
                    href={`/documents/${encodeURIComponent(destinationOrder)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "inherit", fontWeight: 800, textDecoration: "underline" }}
                  >
                    {destinationOrder}
                  </Link>
                ))}
              </Stack>
            )}
          </Stack>
        </Alert>
      </Snackbar>
    </>
  );
}

function SelectedDocumentsList({
  documents,
  onRemove,
  onReorder,
  onClear,
  onToggleSort,
  sortNewestFirst = true
}: {
  documents: OrderDocumentDetail[];
  onRemove: (documentId: number) => void;
  onReorder: (result: DropResult) => void;
  onClear?: () => void;
  onToggleSort?: () => void;
  sortNewestFirst?: boolean;
}) {
  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "primary.dark" }}>
            Selected Documents
          </Typography>
          <Box
            sx={{
              minWidth: 22,
              height: 22,
              px: 0.75,
              borderRadius: 1,
              backgroundColor: "primary.dark",
              color: "common.white",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 900
            }}
          >
            {documents.length}
          </Box>
        </Stack>
        {onClear && (
          <Button
            size="small"
            color="inherit"
            onClick={onClear}
            disabled={documents.length === 0}
            sx={{ fontWeight: 700, textTransform: "none", color: "primary.dark" }}
          >
            Clear Selected
          </Button>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        {onToggleSort ? (
          <Button
            size="small"
            color="inherit"
            onClick={onToggleSort}
            disabled={documents.length < 2}
            endIcon={<Iconify icon="eva:swap-fill" width={16} />}
            sx={{ fontWeight: 700, textTransform: "none", color: "text.secondary" }}
          >
            {sortNewestFirst ? "Sort New to Old" : "Sort Old to New"}
          </Button>
        ) : (
          <Box />
        )}
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
          Click &amp; Drag to Reorder
        </Typography>
      </Stack>

      <DragDropContext onDragEnd={onReorder}>
        <Droppable droppableId="selected-documents">
          {(provided) => (
            <Stack
              spacing={1}
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1
              }}
            >
              {documents.length === 0 && (
                <Typography
                  sx={{
                    minHeight: 72,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "primary.dark",
                    fontWeight: 800,
                    textAlign: "center"
                  }}
                >
                  Please select documents
                </Typography>
              )}
              {documents.map((document, index) => (
                <Draggable key={document.id} draggableId={`document-${document.id}`} index={index}>
                  {(dragProvided, snapshot) => (
                    <Stack
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: (theme) =>
                          snapshot.isDragging
                            ? alpha(theme.palette.primary.main, 0.08)
                            : alpha(theme.palette.grey[500], 0.06),
                        boxShadow: snapshot.isDragging ? "0 8px 24px rgba(22, 28, 36, 0.16)" : "none",
                        ...dragProvided.draggableProps.style
                      }}
                    >
                      <IconButton
                        size="small"
                        {...dragProvided.dragHandleProps}
                        sx={{
                          color: "text.secondary",
                          cursor: snapshot.isDragging ? "grabbing" : "grab",
                          flexShrink: 0
                        }}
                      >
                        <Iconify icon="eva:menu-2-fill" width={18} />
                      </IconButton>
                      <Typography
                        variant="body2"
                        sx={{
                          display: { xs: "none", md: "block" },
                          width: 20,
                          flexShrink: 0,
                          color: "#ff4842",
                          fontWeight: 800,
                          textAlign: "center"
                        }}
                      >
                        {index + 1}
                      </Typography>
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 700, overflowWrap: "anywhere" }}>
                        {document.documentDescription}
                      </Typography>
                      <IconButton size="small" onClick={() => onRemove(document.id)}>
                        <Iconify icon="eva:close-fill" />
                      </IconButton>
                    </Stack>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    </Stack>
  );
}

function getSelectedActionTitle(action: SelectedDocumentAction | null) {
  switch (action) {
    case "mergePrint":
      return "Merge & Print";
    case "merge":
      return "Merge";
    case "copy":
      return "Copy";
    case "delete":
      return "Delete";
    default:
      return "";
  }
}

function buildDocumentDescriptionFromForm(data: { description: string; additionalDescription: string }) {
  if (data.description === "Other") {
    return data.additionalDescription.trim();
  }

  const additionalDescription = data.additionalDescription.trim();
  return additionalDescription ? `${data.description} ${additionalDescription}` : data.description;
}

function sortDocumentCategories(categories: OrderDocumentCategory[]) {
  return [...categories]
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((category) => ({
      ...category,
      subcategories: [...category.subcategories]
        .sort((left, right) => left.name.localeCompare(right.name))
        .map((subcategory) => ({
          ...subcategory,
          descriptions: [...(subcategory.descriptions ?? [])].sort((left, right) => left.localeCompare(right))
        }))
    }));
}

function downloadBase64File(base64: string, fileName: string, contentType: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = Array.from(byteCharacters, (character) => character.charCodeAt(0));
  downloadBlob(new Blob([new Uint8Array(byteNumbers)], { type: contentType }), fileName);
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
